import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useServiceContext, ServiceContextType } from '../../root'
import { useHelpPage } from '../../services/helpPage'
import { useNavigate } from 'react-router'
import { ROUTE_PATHS } from '../../constants/routes'
import Img from '../Img'
import ModalVersionInfo from './ModalVersionInfo'
import { authApiService } from '../../services/api/auth'

const Header: React.FC = () => {
  const context = useServiceContext()
  const { service, constant } = context
  const { translate } = service.filter
  const [isHelpOpen, setIsHelpOpen] = useState(false)
  const [isUserOpen, setIsUserOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [casHomepageUrl, setCasHomepageUrl] = useState<string>('')
  const navigate = useNavigate()
  const helpPage = useHelpPage()
  const headerRef = useRef<HTMLElement>(null)

  const logout = async () => {
    const shouldNavigateToLogin = await service.auth.logout()
    if (shouldNavigateToLogin) {
      navigate(ROUTE_PATHS.LOGIN)
    }
  }

  // User info
  const user = service.auth.getUser()

  // Fetch CAS homepage URL if needed (for IMEDIDATA users)
  useEffect(() => {
    if (service.auth.getAuthType() === constant.user.authType.IMEDIDATA) {
      authApiService.getIMedidataBaseUrl().then((data) => {
        if (data?.imedidataBaseUrl) setCasHomepageUrl(data.imedidataBaseUrl)
      })
    }
  }, [service.auth, constant.user.authType.IMEDIDATA])

  // Close dropdowns on click outside (matches Bootstrap behavior in legacy)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(event.target as Node)) {
        setIsHelpOpen(false)
        setIsUserOpen(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  // Add non-checkmated class if not checkmated, as in legacy
  const isCheckmated = service.checkmate?.isCheckmated?.() ?? true
  const headerClass = `rcm navbar navbar-default${!isCheckmated ? ' non-checkmated' : ''}`
  const menuClass = `dropdown-menu${isHelpOpen ? ' show' : ''}`
  const userMenuClass = `dropdown-menu${isUserOpen ? ' show' : ''}`

  return (
    <header id="fheader" className={headerClass} ref={headerRef}>
      <div className="container-fluid">
        <div className="navbar-header">
          <button
            aria-expanded="false"
            className="navbar-toggle collapsed"
            data-target="#mcc-header"
            data-toggle="collapse"
            type="button"
          >
            <span className="sr-only">Toggle Navigation</span>
            <span className="icon-bar"></span>
            <span className="icon-bar"></span>
            <span className="icon-bar"></span>
          </button>
          <div className="logos">
            <a
              className="navbar-brand"
              href={'/'}
              onClick={(e) => {
                e.preventDefault()
                navigate('/')
              }}
            >
              <div className="mcc-logo">
                <Img src="/assets/images/Medidata_Logo_white.svg" id="small-logo" width={115} height={18} />
              </div>
            </a>
          </div>
        </div>
        <div className="collapse navbar-collapse" id="mcc-header">
          {service.auth.getAuthType() === constant.user.authType.IMEDIDATA ? (
            <ul className="nav navbar-nav nav-primary" id="platform-navigation" role="navigation">
              <li className="home">
                <a className="home-link" href={casHomepageUrl}>
                  <span className="fa fa-home"></span>
                </a>
              </li>
            </ul>
          ) : null}
          <ul className="nav navbar-nav navbar-right">
            <li id="help">
              <a
                className="dropdown-toggle"
                href="#"
                data-toggle="dropdown"
                onClick={(e) => {
                  e.preventDefault()
                  setIsHelpOpen((open) => !open)
                  setIsUserOpen(false)
                }}
              >
                {translate('HELP_TITLE')}
                <span className="caret ml4"></span>
              </a>
              <ul className={menuClass} aria-labelledby="dropdownMenuButton">
                <li className="dropdown-item">
                  <a href={helpPage.getHelpUrl()} target="_blank" rel="noopener noreferrer">
                    {translate('HELP_LINK_THIS_PAGE')}
                  </a>
                </li>
                <li className="dropdown-item">
                  <a href={helpPage.getKnowledgeSpaceUrl()} target="_blank" rel="noopener noreferrer">
                    {translate('HELP_LINK_KNOWLEDGE_HUB_HOME')}
                  </a>
                </li>
                <li className="dropdown-item">
                  <a href={helpPage.getVideosUrl()} target="_blank" rel="noopener noreferrer">
                    {translate('HELP_LINK_SHOW_VIDEOS')}
                  </a>
                </li>
                <li className="dropdown-item">
                  <a href={helpPage.getSupportCenterUrl()} target="_blank" rel="noopener noreferrer">
                    {translate('HELP_LINK_CENTER')}
                  </a>
                </li>
                <li className="dropdown-item">
                  <a
                    onClick={(e) => {
                      e.preventDefault()
                      setIsModalOpen(true)
                    }}
                    className="cursor-pointer"
                    href="#"
                  >
                    {translate('HELP_LINK_VERSION_INFORMATION')}
                  </a>
                </li>
              </ul>
            </li>
            <li id="user">
              <a
                className="dropdown-toggle"
                href="#"
                id="name"
                data-toggle="dropdown"
                aria-haspopup="true"
                onClick={(e) => {
                  e.preventDefault()
                  setIsUserOpen((open) => !open)
                  setIsHelpOpen(false)
                }}
              >
                {user ? `${user.firstName} ${user.lastName}` : ''}
                <span className="caret ml4"></span>
              </a>
              <ul className={userMenuClass} aria-labelledby="dropdownMenuButton" id="user-dropdown">
                <li className="dropdown-item">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      navigate('settings/my-edge-rcm')
                    }}
                  >
                    {translate('NAV_EDIT_SETTINGS')}
                  </a>
                </li>
                <li className="dropdown-item">
                  <a
                    onClick={(e) => {
                      e.preventDefault()
                      logout()
                    }}
                    id="logout"
                    className="cursor-pointer"
                    href="#"
                  >
                    <i className="fa fa-power-off mr5"></i>
                    {translate('NAV_LOG_OUT')}
                  </a>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </div>
      <div>
        {isModalOpen && (
          <ModalVersionInfo show={isModalOpen} onHide={() => setIsModalOpen(false)} versionDetails={context} />
        )}
      </div>
    </header>
  )
}

export default Header
